/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronRightIcon, ChevronDoubleLeftIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { getMonitorAgents } from "@/api/monitor";
import { getSNMPDevices } from "@/api/snmp";
import { getDockerHosts } from "@/api/docker";
import { getRouters } from "@/api/routers";
import { requestFocus, useSelection } from "@/utils/sidebarFocus";
import { BrandMark } from "@/components/common/BrandMark";

interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  shortLabel?: string;
}

interface SubItem {
  id: string;
  label: string;
  /** optional status dot colour class */
  state?: "up" | "down" | "warn" | "idle";
}

interface SidebarProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** Action controls (theme, settings, logout…) pinned to the bottom. */
  footer?: React.ReactNode;
  /** Icon-only rail. Owned by the parent so the page offset can follow it. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

const SPRING = {
  type: "spring" as const,
  stiffness: 520,
  damping: 38,
} as const;

/**
 * Desktop-only sidebar. Device categories expand into a real, data-driven list
 * of their devices; clicking one switches to that tab AND selects the device
 * (via the focus bus, see utils/sidebarFocus). Mobile keeps its bottom tab bar.
 */
export const Sidebar: React.FC<SidebarProps> = ({
  tabs,
  activeTab,
  onTabChange,
  footer,
  collapsed = false,
  onToggleCollapsed,
}) => {
  // The group of the page you are on is open by default, so the item you are
  // viewing is visible; once you toggle a group yourself that choice sticks
  // for that page.
  const [exp, setExp] = useState<{ page: string; id: string | null }>({ page: "", id: null });
  const expanded = exp.page === activeTab ? exp.id : activeTab;
  const selection = useSelection();

  // ...and keep the highlighted item in view when the list is long.
  const activeSubRef = useRef<HTMLButtonElement | null>(null);
  const activeSubId = selection[activeTab];
  useEffect(() => {
    activeSubRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeSubId, activeTab]);

  // Shared cache with the tabs themselves (same query keys), so no extra fetch.
  const { data: agents = [] } = useQuery({ queryKey: ["monitor-agents"], queryFn: getMonitorAgents, staleTime: 30_000 });
  const { data: switches = [] } = useQuery({ queryKey: ["snmp-devices"], queryFn: getSNMPDevices, staleTime: 30_000 });
  const { data: dockerHosts = [] } = useQuery({ queryKey: ["docker-hosts"], queryFn: getDockerHosts, staleTime: 30_000 });
  const { data: routers = [] } = useQuery({ queryKey: ["routers"], queryFn: getRouters, staleTime: 30_000 });

  // scope = the focus channel the target tab listens on (see each *Tab).
  const subItemsFor = (tabId: string): { scope: string; items: SubItem[] } => {
    switch (tabId) {
      case "monitor":
        return { scope: "monitor", items: agents.map((a) => ({ id: String(a.id), label: a.name })) };
      case "switches":
        return { scope: "switches", items: switches.map((d) => ({ id: String(d.id), label: d.name })) };
      case "docker":
        return { scope: "docker", items: dockerHosts.map((h) => ({ id: String(h.id), label: h.name })) };
      case "routers":
        return {
          scope: "routers",
          items: routers.map((r) => ({
            id: r.id,
            label: r.name,
            state: r.online ? "up" : "down",
          })),
        };
      case "network":
        return {
          scope: "network",
          items: [
            { id: "overview", label: "Overview" },
            { id: "speedtest", label: "Speed test" },
            { id: "traceroute", label: "Traceroute" },
            { id: "requests", label: "Requests" },
          ],
        };
      default:
        return { scope: "", items: [] };
    }
  };

  return (
    <aside
      className={cn(
        "hidden sm:flex fixed z-30 left-0 top-0 bottom-0 flex-col transition-[width] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
        collapsed ? "w-[64px]" : "w-[220px]",
        "bg-[#faf8f3] dark:bg-[#1c1a17]",
        "border-r border-gray-200/70 dark:border-white/10",
      )}
      aria-label="Primary"
    >
      {/* Collapse toggle: a round handle on the rail's edge, level with the brand */}
      {onToggleCollapsed && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition-colors hover:text-gray-800 dark:border-white/10 dark:bg-[#23201c] dark:hover:text-gray-100"
        >
          <ChevronDoubleLeftIcon
            className={cn("h-3 w-3 transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]", collapsed && "rotate-180")}
          />
        </button>
      )}

      {/* Brand: the mark never moves; only the name fades */}
      <div className="flex h-12 flex-none items-center gap-3 overflow-hidden whitespace-nowrap border-b border-gray-200 px-[18px] dark:border-gray-700">
        <BrandMark />
        <span
          className={cn(
            "flex-1 truncate text-[14px] font-semibold tracking-tight text-gray-900 transition-opacity duration-200 dark:text-gray-100",
            collapsed && "opacity-0",
          )}
        >
          Nyrvex
        </span>
      </div>

      {/* Search (opens the command palette) */}
      <div className="px-2 pt-3 pb-1">
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("nyrvex:palette"))}
          title="Search"
          className="flex w-full items-center gap-2.5 overflow-hidden whitespace-nowrap rounded-[10px] border border-gray-200 bg-gray-100/60 py-2 pl-[16px] pr-2.5 text-left text-[13.5px] text-gray-400 transition-colors hover:text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:text-gray-300"
        >
          <svg className="h-3.5 w-3.5 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3-3" strokeLinecap="round" />
          </svg>
          <span className={cn("flex-1 transition-opacity duration-200", collapsed && "opacity-0")}>Search…</span>
          <kbd className={cn("flex-none rounded-[3px] border border-gray-300 px-1 text-[10px] transition-opacity duration-200 dark:border-gray-600", collapsed && "opacity-0")}>⌘K</kbd>
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-small py-2">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const { scope, items } = subItemsFor(tab.id);
          const hasSub = items.length > 0;
          const isOpen = expanded === tab.id && !collapsed;
          return (
            <div key={tab.id}>
              <button
                type="button"
                onClick={() => {
                  onTabChange(tab.id);
                  if (hasSub && !collapsed) setExp({ page: tab.id, id: expanded === tab.id ? null : tab.id });
                }}
                aria-current={isActive ? "page" : undefined}
                aria-expanded={hasSub ? isOpen : undefined}
                title={tab.label}
                className={cn(
                  "group relative flex w-full items-center gap-3 overflow-hidden whitespace-nowrap px-[22px] py-3 text-left",
                  "text-[14px] font-medium transition-colors duration-100 outline-none",
                  "focus-visible:text-gray-900 dark:focus-visible:text-white",
                  isActive
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100",
                )}
              >
                {isActive && (
                  <motion.span
                    layoutId="sidebarActiveBar"
                    className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-emerald-600 dark:bg-emerald-500"
                    transition={SPRING}
                  />
                )}
                <span className={cn("flex h-[20px] w-[20px] flex-none items-center justify-center [&_svg]:h-[19px] [&_svg]:w-[19px]", isActive && "text-emerald-600 dark:text-emerald-400")}>
                  {tab.icon}
                </span>
                <span className={cn("flex-1 truncate transition-opacity duration-200", collapsed && "opacity-0")}>{tab.label}</span>
                {hasSub && (
                  <span className={cn("flex items-center gap-1.5 transition-opacity duration-200", collapsed && "opacity-0")}>
                    <span className="text-[10.5px] tabular-nums text-gray-400 dark:text-gray-500">{items.length}</span>
                    <ChevronRightIcon
                      className={cn(
                        "h-3.5 w-3.5 text-gray-400 transition-transform duration-200",
                        isOpen ? "rotate-90" : "",
                      )}
                    />
                  </span>
                )}
              </button>

              <AnimatePresence initial={false}>
                {hasSub && isOpen && (
                  <motion.ul
                    key="sub"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    {items.map((sub) => {
                      const current = isActive && selection[scope] === sub.id;
                      return (
                      <li key={sub.id}>
                        <button
                          type="button"
                          ref={current ? activeSubRef : undefined}
                          aria-current={current ? "true" : undefined}
                          onClick={() => {
                            requestFocus(scope, sub.id);
                            onTabChange(tab.id);
                          }}
                          title={sub.label}
                          className={cn(
                            "relative flex w-full items-center gap-2 py-2 pl-11 pr-3 text-left",
                            "text-[13.5px] transition-colors duration-100 outline-none",
                            current
                              ? "bg-emerald-500/10 font-semibold text-gray-900 dark:bg-emerald-400/10 dark:text-white"
                              : "text-gray-500 hover:text-gray-900 hover:bg-gray-100/50 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800/30",
                          )}
                        >
                          {current && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full bg-emerald-500" aria-hidden="true" />}
                          <span
                            className={cn(
                              "absolute left-[23px] h-1.5 w-1.5 rounded-full",
                              current && "ring-2 ring-emerald-500/40",
                              sub.state === "up" && "bg-emerald-500",
                              sub.state === "down" && "bg-red-500",
                              sub.state === "warn" && "bg-amber-500",
                              (!sub.state || sub.state === "idle") && "bg-current opacity-40",
                            )}
                          />
                          <span className="truncate">{sub.label}</span>
                        </button>
                      </li>
                      );
                    })}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>

      {/* Footer actions (re-flow between row and column, so fade the swap in) */}
      {footer && (
        <div className="flex-none overflow-hidden border-t border-gray-200 py-1.5 dark:border-gray-700">{footer}</div>
      )}
    </aside>
  );
};
